import React, { useState } from "react";
import { BookOpen } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useSite } from "../context/SiteContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const FALLBACK = {
  note: "Plats maison en bocal · à régler sur place, sur réservation",
  courses: [
    { key: "entrees", label: "Entrées", price: 7, items: [
      { name: "Rillettes de saumon", desc: "Des rillettes savoureuses à base de saumon." },
      { name: "Pâté de campagne", desc: "Un grand classique de la cuisine française." },
    ] },
    { key: "plats", label: "Plats", price: 13, items: [
      { name: "Rougail saucisse de Dinan", desc: "Un plat généreux et légèrement épicé." },
      { name: "Cuisse de canard confite en parmentier", desc: "Canard confit accompagné d'un savoureux parmentier." },
      { name: "Risotto aux crevettes", desc: "Un risotto crémeux accompagné de crevettes." },
      { name: "Lasagnes bretonnes végétariennes", desc: "Des lasagnes gourmandes aux saveurs bretonnes, sans viande." },
      { name: "Crumble de légumes de saison", desc: "Des légumes de saison recouverts d'un crumble croustillant." },
    ] },
    { key: "desserts", label: "Dessert", price: 7, items: [
      { name: "Riz au lait d'autrefois", desc: "Un dessert traditionnel, doux et crémeux." },
      { name: "Fondant au chocolat", desc: "Un cœur coulant au chocolat intense." },
      { name: "Soupe de fraise et framboise", desc: "Une soupe de fruits rouges fraîche et parfumée." },
    ] },
  ],
  drinks: [],
};

const LABELS = {
  fr: { button: "Voir les menus", title: "Nos menus", cuisine: "La Cuisine de Mon Père", galettes: "Galettes & Crêpes La Bégaudière", galettesNote: "Galettes bretonnes, crêpes desserts et boissons · sur réservation à l'avance.", drinks: "Boissons", note: "Menu proposé à titre indicatif : les plats peuvent varier selon les stocks. Nous proposons également des entrées, plats et desserts de saison ou selon la livraison de la semaine." },
  en: { button: "View the menus", title: "Our menus", cuisine: "La Cuisine de Mon Père", galettes: "Galettes & Crêpes La Bégaudière", galettesNote: "Breton galettes, dessert crêpes and drinks · by advance reservation.", drinks: "Drinks", note: "Sample menu: dishes may vary depending on stock. We also offer seasonal starters, mains and desserts, or according to the week's delivery." },
};

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",").replace(",00", "")} €`;

export const BocauxMenu = ({ className = "" }) => {
  const { lang } = useI18n();
  const { settings } = useSite();
  const l = LABELS[lang] || LABELS.fr;
  const menu = (settings.bocaux_menu && settings.bocaux_menu.courses) ? settings.bocaux_menu : FALLBACK;
  const gSections = (settings.galettes_menu && settings.galettes_menu.sections) ? settings.galettes_menu.sections : [];
  const [open, setOpen] = useState(false);

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`inline-flex items-center gap-1.5 text-brand text-sm font-medium hover:underline underline-offset-4 transition-colors ${className}`} data-testid="bocaux-menu-trigger">
        <BookOpen size={15} strokeWidth={1.75} /> {l.button}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="bocaux-menu-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-ink-900">{l.title}</DialogTitle>
          </DialogHeader>
          <h3 className="font-heading text-xl text-brand mt-1" data-testid="menu-cuisine-title">{l.cuisine}</h3>
          <p className="text-sm text-ink-400 -mt-1">{menu.note || FALLBACK.note}</p>
          <p className="text-xs text-ink-600 leading-relaxed bg-beige/60 rounded-lg p-3" data-testid="bocaux-story">
            Le chef <b>Thierry Teffaine</b> et sa femme <b>Magali</b> ont marqué la scène gastronomique dinannaise pendant plus de 17 ans avec leur restaurant <i>Les 3 Lunes</i>, devenu une véritable référence locale. Cuisinier passionné et formateur reconnu (cuisson sous-vide, basse température, techniques douces), Thierry partage son savoir-faire depuis des années. De là est née une idée simple : rendre la cuisine du chef accessible partout, avec le même niveau d'exigence que dans un grand restaurant.
          </p>
          <div className="space-y-6 mt-2">
            {(menu.formulas && menu.formulas.length > 0) && (
              <div data-testid="bocaux-menu-formulas">
                <div className="flex items-baseline justify-between border-b border-black/5 pb-2 mb-3">
                  <h3 className="font-heading text-lg text-ink-900">Nos formules</h3>
                </div>
                <ul className="space-y-2">
                  {menu.formulas.map((f, i) => (
                    <li key={i} className="flex justify-between gap-3 text-sm"><span className="text-ink-800"><b>{f.label}</b>{f.desc ? <span className="text-ink-500"> — {f.desc}</span> : null}</span><span className="text-brand font-medium shrink-0">{eur(f.price)}</span></li>
                  ))}
                </ul>
              </div>
            )}
            {(menu.courses || []).map((c) => (
              <div key={c.key || c.label} data-testid={`bocaux-menu-section-${c.key || c.label}`}>
                <div className="flex items-baseline justify-between border-b border-black/5 pb-2 mb-3">
                  <h3 className="font-heading text-lg text-ink-900">{c.label}</h3>
                  <span className="font-heading text-brand">{eur(c.price)}</span>
                </div>
                <ul className="space-y-3">
                  {(c.items || []).map((it, i) => (
                    <li key={i} className="flex items-start gap-3">
                      {it.img ? <img src={it.img} alt={it.name} loading="lazy" className="w-12 h-12 rounded-lg object-cover shrink-0" /> : null}
                      <div>
                        <p className="font-body text-ink-900 text-sm font-medium">{it.name}</p>
                        {it.desc ? <p className="font-body text-ink-500 text-xs leading-relaxed">{it.desc}</p> : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {(menu.drinks && menu.drinks.length > 0) && (
              <div data-testid="bocaux-menu-drinks">
                <div className="flex items-baseline justify-between border-b border-black/5 pb-2 mb-3">
                  <h3 className="font-heading text-lg text-ink-900">{l.drinks}</h3>
                  <span className="text-xs text-ink-400">{menu.drinks_note || "33 cl"}</span>
                </div>
                <ul className="space-y-2">
                  {menu.drinks.map((d, i) => (
                    <li key={i} className="flex justify-between text-sm text-ink-800"><span>{d.name}</span><span className="text-brand">{eur(d.price)}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <p className="mt-6 pt-4 border-t border-black/5 text-xs text-ink-400 italic leading-relaxed" data-testid="bocaux-menu-note">{l.note}</p>

          {gSections.length > 0 && (
            <div className="mt-8 pt-6 border-t-2 border-brand/20" data-testid="menu-galettes-block">
              <h3 className="font-heading text-xl text-brand">{l.galettes}</h3>
              <p className="text-sm text-ink-400 mb-4">{l.galettesNote}</p>
              <div className="space-y-6">
                {gSections.map((sec) => (
                  <div key={sec.key || sec.label} data-testid={`galettes-menu-section-${sec.key || sec.label}`}>
                    <div className="flex items-baseline justify-between border-b border-black/5 pb-2 mb-3">
                      <h4 className="font-heading text-lg text-ink-900">{sec.label}</h4>
                    </div>
                    <ul className="space-y-3">
                      {(sec.items || []).map((it, i) => (
                        <li key={i} className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-body text-ink-900 text-sm font-medium">{it.name}</p>
                            {it.desc ? <p className="font-body text-ink-500 text-xs leading-relaxed">{it.desc}</p> : null}
                          </div>
                          <span className="font-heading text-brand shrink-0">{eur(it.price)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BocauxMenu;
